import type { DateString } from '../../../dateString';
import type { RequestDataForSlot } from '@skyscanner/strevda-gam';
import type { AdvertTrackingInfo, PerformanceEventsPayloads } from '@skyscanner/strevda-tracking';
import type { PLACE_RESULT_FEATURE_TYPE } from '@web-platform/shared-universal-constants/src/placeResultFeatureType';
import type { PLACE_RESULT_TYPE } from '@web-platform/shared-universal-constants/src/places';
import type {
  EntityId,
  Nullable,
  SkyNodeCode,
} from '@web-platform/shared-universal-types-utils/src/utils';

export const FLEXIBLE_TYPE = '@type';

export type PlaceResultFeatureType = keyof typeof PLACE_RESULT_FEATURE_TYPE;

export const LOCATION_TYPE = {
  EVERYWHERE: 'everywhere',
  ENTITY: 'entity',
} as const;

export const DATE_TYPE = {
  ANYTIME: 'anytime',
  MONTH: 'month',
  DATE: 'date',
  DATE_RANGE: 'date-range',
} as const;

export const PILL_CATEGORY = {
  NON_CATEGORIZED: 'NON_CATEGORIZED',
  VIBES: 'VIBES',
} as const;

export type HotelMetadata = {
  hotelStars: number;
  quotesAccessibilityLabel: string;
  quotesLabel: string;
};

// The enable/disable/failed status of hotel/flights
export type PlaceResultFeatures = {
  flightsIndicative?: PlaceResultFeatureType;
  hotelsIndicative?: PlaceResultFeatureType;
  images?: string;
  ads?: string;
};

// Request payload for combined explore(unfocused search)
export type RadarUnfocusedSearch = {
  legs: RadarUnfocusedSearchLeg[];
};

// Type definition of leg for Combined Explore request payload
export type RadarUnfocusedSearchLeg = {
  legOrigin: FlexibleLocation;
  legDestination: FlexibleLocation;
  dates: FlexibleDates;
};

// A location designator that offers multiple ways of filtering results by location
export type FlexibleLocation =
  | {
      [FLEXIBLE_TYPE]: typeof LOCATION_TYPE.EVERYWHERE;
    }
  | {
      [FLEXIBLE_TYPE]: typeof LOCATION_TYPE.ENTITY;
      entityId: string;
    };

// A designator for a set of acceptable dates for a trip
export type FlexibleDates =
  | {
      [FLEXIBLE_TYPE]: typeof DATE_TYPE.ANYTIME;
    }
  | {
      [FLEXIBLE_TYPE]: typeof DATE_TYPE.MONTH;
      year: YearType;
      month: MonthType;
    }
  | {
      [FLEXIBLE_TYPE]: typeof DATE_TYPE.DATE;
      year: YearType;
      month: MonthType;
      day: DayType;
    }
  | {
      [FLEXIBLE_TYPE]: typeof DATE_TYPE.DATE_RANGE;
      start: DateType;
      end: DateType;
    };

type YearType = number;
type MonthType = number;
type DayType = number;

type DateType = {
  year: YearType;
  month: MonthType;
  day: DayType;
};

// Response payload for radar combined explore(unfocused search)
export type RadarUnfocusedSearchPayload =
  RadarEverywherePayload | RadarCountryPayload | RadarOriginSelectionPayload;

// Response for the Everywhere use case. This use case is triggered whenever as long as the destination is set to 'everywhere', which is the default initial state for Combined Explore.
export type RadarEverywherePayload = {
  context: ContextResponse;
  everywhereDestination: BucketedLocationsResponse;
};

// Response for the Country use case. This response will provide a list of city results within a previously selected country destination.
export type RadarCountryPayload = {
  context: ContextResponse;
  countryDestination: BucketedLocationsResponse;
  destinationExplorer?: Nullable<LocationExplorerResponse>;
};

// Response for the Origin Selection use case. This response will provide a list of city origins to disambiguate a country origin.
export type RadarOriginSelectionPayload = {
  context: ContextResponse;
  countryOrigin: BucketedLocationsResponse;
};

// State and metadata for the Combined Explore response.
type ContextResponse = {
  // Combined Results support a variety of status codes, to indicate completeness states. For Combined Explore, this will always be set to 'complete', as it doesn't support polling."
  status: string;
  sessionId: string;
};

export type LocationDestinationResponse = {
  context: VerticalContext;
  location: Location;
  parentLocation?: Location;
};

// A Destination Explorer component
export type LocationExplorerResponse = {
  context: VerticalContext;
  creativeFields: {
    headerImage: Image;
    headerImageMobile?: Image;
    partnerLogo: Image;
    partnerName: string;
    partnerBillingInfo?: {
      pioPartnerName: string;
      pioAgencyName?: string;
    };
    destinationDescription?: {
      headline?: string;
      subHeadline?: string;
      body?: string;
    };
    videoLink?: string;
    contentContainer?: {
      headerText?: string;
      cardsList?: Array<{
        headline: string;
        description: string;
        image: Image;
        ctaLink: string;
      }>;
    };
    imageGallery?: Array<{
      image: Image;
      credit?: string;
      description?: string;
      location?: string;
    }>;
  };
  trackingPixels: TrackingPixels;
  trackingContext: TrackingContext;
  performanceEventsPayloads?: PerformanceEventsPayloads;
};

// Type definition for destinations field in Country&Everywhere Search result.
export type BucketedLocationsResponse = {
  context: VerticalContext;
  buckets: BucketInfo[];
  results: PlaceResult[];
  features: PlaceResultFeatures;
  hotelMetadata?: HotelMetadata;
};
export type BucketInfo = {
  id: string;
  label: string;
  resultIds: string[];
  flightQuotes: string;
  hotelQuotes?: string;
  totalTripQuotes?: string;
  category: keyof typeof PILL_CATEGORY;
};
export type PlaceResult =
  | {
      id: string;
      type: typeof PLACE_RESULT_TYPE.LOCATION;
      content: PlaceLocationResult;
    }
  | {
      id: string;
      type: typeof PLACE_RESULT_TYPE.ADVERT;
      content: PlaceAdvertResult;
    };

export type PlaceAdvertResult = {
  creative?: AdvertResultCreative;
  trackingPixels: TrackingPixels;
  trackingContext: TrackingContext;
  gamRequestData?: RequestDataForSlot;
  performanceEventsPayloads?: PerformanceEventsPayloads;
};

export type PlaceLocationResult = {
  location: Location;
  parentLocation?: Location;
  flightQuotes?: Nullable<FlightQuotesForLocation>;
  flightRoutes?: Nullable<FlightRoutesForLocation>;
  hotelQuotes?: Nullable<HotelQuotesForLocation>;
  totalTripQuotes?: Nullable<TotalTripQuotesForLocation>;
  image?: Nullable<Image>;
  inlineCreative?: InlineCreative;
};

export type PlaceLocationResultPreferCheapestPrice = PlaceLocationResult & {
  preferCheapestDisplay?: boolean;
};

type FlightQuotesForLocation = Record<string, FlightQuoteMinimal>;
type FlightRoutesForLocation = { directFlightsAvailable: boolean };
type HotelQuotesForLocation = Record<string, HotelQuote>;
type TotalTripQuotesForLocation = Record<string, TotalTripQuote>;

// State and metadata for the Combined Explore vertical.
type VerticalContext = {
  // Combined Results support a variety of status codes, to indicate different errors and completeness states. Combined Explore has no polling, nor any significant error states that deserve special treatment, so, in practice, it will either return 'complete' or outright exclude the inventory from the response.
  status?: 'complete';
  sessionId: string;
  // The total number of results contained within the vertical.
  totalResults: number;
};

// Image object
type Image = {
  url: string;
};

// Details for a geo location
export type Location = {
  id: EntityId;
  skyCode: SkyNodeCode;
  name: string;
  type: 'Nation' | 'City' | 'Airport';
  continent: {
    code: string;
    name: string;
  };
};

export type MonthsInfo = {
  year: number;
  month: number;
  monthLabel: string;
  selected: boolean;
};

export type UnfocusedQuoteBucketInfo = {
  id: string;
  label: string;
  resultIds: string[];
  category: keyof typeof PILL_CATEGORY;
};

// Details for a flight quote
export type FlightQuote = FlightQuoteMinimal & {
  outboundLeg: FlightLeg;
  inboundLeg?: FlightLeg;
};

// Details for a flight quote(minimal info)
export type FlightQuoteMinimal = {
  // A price, already formatted in the user's locale
  price: string;
  // The same price in integer form
  rawPrice: number;
  // Indicates if the quote corresponds to a direct flight (true) or one with 1+ stops (false).
  direct: boolean;
  pocExplore815?: {
    duration: number;
    stops: number;
  };
};

// A leg quote for a flight
export type FlightLeg = {
  originAirport: Location;
  destinationAirport: Location;
  // Local to the timezone of the origin airport, in YYYY-MM-DD format. e.g. 2022-10-01
  localDepartureDate: DateString;
  localDepartureDateLabel: string;
};

// Details for a hotel quote
export type HotelQuote = {
  price: string;
};

// Details for a total trip quote
type TotalTripQuote = {
  price: string;
  rawPrice: number;
};

export type CreativeFields = {
  partnerLogo: Image;
  partnerName: string;
  locationImageDesktop?: Image;
  locationImageMobile?: Image;
  brandMessage?: string;
  hubPageUrl?: string;
  partnerId?: string;
  partnerBillingInfo?: {
    pioPartnerName: string;
    pioAgencyName?: string;
  };
};

// An advert creative which contains information to render the ad
type InlineCreative = {
  creativeFields: CreativeFields;
  trackingPixels: TrackingPixels;
  trackingContext: TrackingContext;
  performanceEventsPayloads?: PerformanceEventsPayloads;
};

// An object that contains the creative information to render a result of type advert
type AdvertResultCreative = StandardNativeDisplay | DoubleNativeDisplay;

type StandardNativeDisplay = {
  type: 'STANDARD_DISPLAY_ADVERT';
  fields: NativeDisplayCommonFields & Partial<FieldsOptionalForStandardDisplayAdvert>;
};

type DoubleNativeDisplay = {
  type: 'DOUBLE_DISPLAY_ADVERT';
  fields: NativeDisplayCommonFields & FieldsOptionalForStandardDisplayAdvert;
};

type NativeDisplayCommonFields = {
  headline: string;
  subHeadline: string;
  layoutType: string;
  redirectUrl: string;
  backgroundImage: {
    url: string;
  };
  fontColour?: string;
  backgroundColour?: string;
};

type FieldsOptionalForStandardDisplayAdvert = {
  hideSponsoredLabel: boolean;
  partnerName: string;
  partnerLogo: {
    url: string;
  };
};

// The creative tracking pixels
export type TrackingPixels = AdvertTrackingInfo['pixels'];

// The creative tracking context
export type TrackingContext = {
  idCreative: string;
  idFormat: string;
  idPlacement: string;
  idTracking: string;
};
